// Archivo: src/services/service.service.js

// 1. Se define la URL base de la API y del almacenamiento para una gestión más fácil.
const API_URL = process.env.REACT_APP_API_URL;
const STORAGE_URL = process.env.REACT_APP_STORAGE_URL;

/**
 * Procesa la respuesta de la API, maneja errores y parsea el JSON.
 * @param {Response} response - El objeto de respuesta de la API.
 * @returns {Promise<Object>} Los datos de la respuesta.
 */
const handleApiResponse = async (response) => {
  if (!response.ok) {
    const errorData = await response.json().catch(() => ({}));
    throw new Error(errorData.message || `Error ${response.status} en la respuesta del servidor.`);
  }
  return response.json();
};

/**
 * Obtiene los datos de un servicio específico por su ID.
 * @param {string} serviceId - El ID del servicio a buscar.
 * @returns {Promise<Object>} Los datos del servicio.
 */
export const getPublicServiceById = async (serviceId) => {
  try {
    const response = await fetch(`${API_URL}/services/${serviceId}`);
    const data = await handleApiResponse(response);
    const service = data.data;

    // 2. Se construye la URL completa del logo a partir de 'logo_path'.
    return {
      ...service,
      logo_url: service.logo_path ? `${STORAGE_URL}/${service.logo_path}` : null,
    };
  } catch (error) {
    console.error(`Error al obtener el servicio con ID ${serviceId}:`, error);
    throw error;
  }
};

/**
 * Obtiene todas las secciones asociadas a un servicio.
 * @param {string} serviceId - El ID del servicio.
 * @returns {Promise<Array>} Una lista de las secciones.
 */
export const getPublicServiceSections = async (serviceId) => {
  try {
    const response = await fetch(`${API_URL}/services/${serviceId}/sections`);
    const data = await handleApiResponse(response);
    return data.data;
  } catch (error) {
    console.error(`Error al obtener las secciones para el servicio ${serviceId}:`, error);
    throw error;
  }
};

/**
 * Obtiene la lista completa de servicios públicos.
 * @returns {Promise<Array>} Un array de objetos de servicio.
 */
export const getPublicServices = async () => {
  try {
    const response = await fetch(`${API_URL}/services`);
    const data = await handleApiResponse(response);
    
    // 3. Se mapean los resultados para construir la URL del logo para cada servicio.
    return data.data.map(service => ({
      ...service,
      logo_url: service.logo_path ? `${STORAGE_URL}/${service.logo_path}` : null,
    }));
  } catch (error) {
    console.error('Error al obtener la lista de servicios:', error);
    throw error;
  }
};

/**
 * Obtiene los datos de una sección específica por su ID.
 * @param {string} sectionId - El ID de la sección.
 * @returns {Promise<Object>} Los datos de la sección.
 */
export const getSectionDetailsById = async (sectionId) => {
  try {
    const response = await fetch(`${API_URL}/sections/${sectionId}`);
    const data = await handleApiResponse(response);
    
    console.log('Raw API response for section details:', data); // Debug log
    
    // Verificar si la respuesta es un array (múltiples secciones) o un objeto (una sección)
    let sectionData;
    if (Array.isArray(data.data)) {
      // Si es un array, buscar la sección específica
      sectionData = data.data.find(section => section.id === parseInt(sectionId));
      if (!sectionData) {
        throw new Error(`Sección con ID ${sectionId} no encontrada`);
      }
    } else {
      // Si es un objeto directo
      sectionData = data.data;
    }
    
    console.log('Section data found:', sectionData); // Debug log
    console.log('YouTube links:', sectionData.youtube_links); // Debug log
    console.log('Documents:', sectionData.documents); // Debug log
    
    // Transformar la estructura para que coincida con lo que espera el frontend
    return {
      details: {
        id: sectionData.id,
        title: sectionData.title,
        description: sectionData.description,
        service_id: sectionData.service_id,
        user_id: sectionData.user_id,
        created_at: sectionData.created_at,
        updated_at: sectionData.updated_at,
        deleted_at: sectionData.deleted_at
      },
      videos: sectionData.youtube_links || [],
      documents: sectionData.documents || []
    };
  } catch (error) {
    console.error(`Error al obtener los detalles de la sección ${sectionId}:`, error);
    throw error;
  }
};

/**
 * Obtiene todas las subsecciones de una sección específica.
 * @param {string} sectionId - El ID de la sección.
 * @returns {Promise<Array>} Una lista de las subsecciones.
 */
export const getSubsectionsBySectionId = async (sectionId) => {
  try {
    const response = await fetch(`${API_URL}/sections/${sectionId}/subsections`);
    const data = await handleApiResponse(response);
    return data.data;
  } catch (error) {
    console.error(`Error al obtener las subsecciones para la sección ${sectionId}:`, error);
    throw error;
  }
};